import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { ExerciseDetailDialog } from '../components/ExerciseDetailDialog';
import { ExerciseMedia, PlanChips, SetInputs } from '../components/WorkoutPlayerParts';
import { useConfirm } from '../contexts/ConfirmContext';
import { useLiveWorkout } from '../lib/useLiveWorkout';
import { GlowBackground } from '../components/GlowBackground';
import { Ring } from '../components/charts';
import { X, Pause, Play, SkipForward, Plus, Info, Check, Flag } from 'lucide-react';
import {
  buildQueue, nextPosition, restAfter, skipTarget, supersetOf, clockNow, pauseClock, resumeClock, formatTime, defaultRepsFor,
  toWorkoutLogExercises, storageKey, loadSaved, saveState, clearSaved, signalRestEnd,
} from '../lib/workoutPlayer';

import { localISODate } from '../lib/utils';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const INITIAL = {
  phase: 'intro', // intro | ready | work | rest | summary
  exIdx: 0,
  setIdx: 0,
  clock: { accumulated: 0, resumedAt: null },
  phaseStartedAt: 0, // session-clock ms when the current work/rest phase began
  restSeconds: 0,
  lastDone: null, // { exIdx, setIdx } of the set being logged during rest
  results: [], // results[exIdx][setIdx] = { reps, weight }
  overallNotes: '',
};

export const WorkoutPlayerPage = () => {
  const { planId, day } = useParams();
  const dayNumber = parseInt(day, 10) || 1;
  const navigate = useNavigate();
  const key = storageKey(planId, dayNumber);

  const [plan, setPlan] = useState(null);
  const [exercisesById, setExercisesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(INITIAL);
  const [saved, setSaved] = useState(() => loadSaved(key));
  const [now, setNow] = useState(Date.now());
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const wakeLock = useRef(null);
  const confirm = useConfirm();

  useEffect(() => {
    (async () => {
      try {
        const [plansRes, exRes] = await Promise.all([
          axios.get(`${API_URL}/api/client/my-plans`, { withCredentials: true }),
          axios.get(`${API_URL}/api/exercises`, { withCredentials: true }),
        ]);
        const byId = {};
        exRes.data.forEach(ex => { byId[ex.id] = ex; });
        setExercisesById(byId);
        setPlan(plansRes.data.find(p => p.id === planId) || null);
      } catch (error) {
        console.error('Error loading workout:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [planId]);

  const queue = useMemo(() => (plan ? buildQueue(plan, dayNumber, exercisesById) : []), [plan, dayNumber, exercisesById]);
  const { phase, exIdx, setIdx, clock } = state;
  const running = !!clock.resumedAt;
  const active = phase === 'ready' || phase === 'work' || phase === 'rest';
  const elapsed = clockNow(clock, now);
  const clockRef = useRef(clock);
  clockRef.current = clock;
  useLiveWorkout({ planId, dayNumber, phase, exIdx, setIdx, queue, running, getElapsed: () => clockNow(clockRef.current) });

  // Tick while the clock runs.
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [running]);

  // Persist progress so a refresh or locked phone doesn't lose the workout -
  // on every state change, and again when the page is hidden so the running
  // clock's latest value isn't lost between state changes.
  const latest = useRef(state);
  const savedRef = useRef(false);
  latest.current = state;
  useEffect(() => {
    const persist = () => {
      const s = latest.current;
      if (['ready', 'work', 'rest', 'summary'].includes(s.phase)) saveState(key, { ...s, clock: pauseClock(s.clock) });
    };
    persist();
    const onHide = () => document.visibilityState === 'hidden' && persist();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', persist);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', persist);
    };
  }, [state, key]);
  // Leaving via in-app navigation (exit button) fires neither event above.
  useEffect(() => () => {
    const s = latest.current;
    if (['ready', 'work', 'rest', 'summary'].includes(s.phase) && !savedRef.current) {
      saveState(key, { ...s, clock: pauseClock(s.clock) });
    }
  }, [key]);

  // Keep the screen on during the workout.
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return undefined;
    const request = async () => {
      try { wakeLock.current = await navigator.wakeLock.request('screen'); } catch { /* not allowed */ }
    };
    const onVisible = () => document.visibilityState === 'visible' && request();
    request();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      wakeLock.current?.release().catch(() => {});
      wakeLock.current = null;
    };
  }, [active]);

  const restLeft = phase === 'rest' ? state.restSeconds * 1000 - (elapsed - state.phaseStartedAt) : 0;

  const goNextAfter = useCallback((s, pos) => {
    const next = nextPosition(queue, pos.exIdx, pos.setIdx);
    if (!next) return { ...s, phase: 'summary', clock: pauseClock(s.clock) };
    return { ...s, phase: 'ready', exIdx: next.exIdx, setIdx: next.setIdx };
  }, [queue]);

  // Rest finished -> beep and show the next set, waiting for START.
  useEffect(() => {
    if (phase === 'rest' && running && restLeft <= 0) {
      signalRestEnd();
      setState(s => goNextAfter(s, { exIdx: s.exIdx, setIdx: s.setIdx }));
    }
  }, [phase, running, restLeft, goNextAfter]);

  const startWorkout = () => setState({ ...INITIAL, phase: 'ready', clock: resumeClock(INITIAL.clock), results: queue.map(() => []) });
  const resumeSaved = () => {
    setState({ ...saved, clock: saved.phase === 'summary' ? saved.clock : resumeClock(saved.clock) });
    setSaved(null);
  };
  const togglePause = () => setState(s => ({ ...s, clock: running ? pauseClock(s.clock) : resumeClock(s.clock) }));
  const startSet = () => setState(s => ({ ...s, phase: 'work', phaseStartedAt: clockNow(s.clock), clock: resumeClock(s.clock) }));

  const setResult = (e, i, value) => setState(s => {
    const results = s.results.map(r => [...r]);
    results[e][i] = value;
    return { ...s, results };
  });

  const finishSet = () => setState(s => {
    const ex = queue[s.exIdx];
    const results = s.results.map(r => [...r]);
    results[s.exIdx][s.setIdx] = results[s.exIdx][s.setIdx] || { reps: defaultRepsFor(ex.reps), weight: ex.weight ?? '' };
    const withResult = { ...s, results, lastDone: { exIdx: s.exIdx, setIdx: s.setIdx } };
    const next = nextPosition(queue, s.exIdx, s.setIdx);
    if (!next) return { ...withResult, phase: 'summary', clock: pauseClock(s.clock) };
    const rest = restAfter(queue, s.exIdx, s.setIdx);
    if (rest <= 0) return { ...withResult, phase: 'ready', ...next };
    return { ...withResult, phase: 'rest', restSeconds: rest, phaseStartedAt: clockNow(s.clock) };
  });

  const skipRest = () => setState(s => goNextAfter(s, { exIdx: s.exIdx, setIdx: s.setIdx }));
  const addRest = () => setState(s => ({ ...s, restSeconds: s.restSeconds + 15 }));
  const skipExercise = () => setState(s => goNextAfter(s, skipTarget(queue, s.exIdx)));
  const finishEarly = async () => {
    const ok = await confirm('Zapiszesz to, co zrobione do tej pory.', {
      title: 'Zakończyć trening?', confirmLabel: 'Zakończ', cancelLabel: 'Trenuję dalej',
    });
    if (ok) setState(s => ({ ...s, phase: 'summary', clock: pauseClock(s.clock) }));
  };
  const exit = async () => {
    if (active || phase === 'summary') {
      const ok = await confirm(
        phase === 'summary'
          ? 'Trening nie jest jeszcze zapisany. Możesz wrócić i zapisać go później.'
          : 'Postęp zostanie zapamiętany i możesz wrócić później.',
        { title: 'Wyjść z treningu?', confirmLabel: 'Wyjdź', cancelLabel: 'Zostań' },
      );
      if (!ok) return;
    }
    navigate(`/client-plan/${planId}`);
  };

  const saveWorkout = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/workout-logs`, {
        plan_id: planId,
        day_number: dayNumber,
        log_date: localISODate(),
        exercises: toWorkoutLogExercises(queue, state.results),
        overall_notes: state.overallNotes || null,
        source: 'player',
        duration_minutes: Math.max(1, Math.round(elapsed / 60000)),
      }, { withCredentials: true });
      savedRef.current = true;
      clearSaved(key);
      toast.success('Trening zapisany. Dobra robota!');
      navigate('/client-dashboard');
    } catch (error) {
      console.error('Error saving workout:', error);
      toast.error('Nie udało się zapisać treningu. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const shell = 'min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] relative';
  // `client-frame`: on phones a locked full-screen window, the phase buttons sit in a bar pinned at the bottom.
  if (loading) {
    return <div className={`${shell} flex items-center justify-center`}>Ładowanie...</div>;
  }
  if (!plan || queue.length === 0) {
    return (
      <div className={`${shell} flex flex-col items-center justify-center gap-4`}>
        <p>{plan ? 'Brak ćwiczeń w tym dniu treningowym.' : 'Plan nie znaleziony.'}</p>
        <button className="cta-ghost rounded-full h-12 px-6" onClick={() => navigate('/client-dashboard')}>Powrót</button>
      </div>
    );
  }

  const totalSets = queue.reduce((n, ex) => n + ex.sets, 0);
  const doneSets = state.results.reduce((n, r) => n + (r || []).filter(Boolean).length, 0);
  const ex = queue[exIdx];
  const upcoming = phase === 'rest' ? nextPosition(queue, exIdx, setIdx) : null;
  const group = supersetOf(queue, exIdx);
  const after = phase === 'ready' || phase === 'work' ? nextPosition(queue, exIdx, setIdx) : null;
  const noRestNext = !!after && restAfter(queue, exIdx, setIdx) <= 0;
  // During rest the client logs the set just done, or every set of the superset round.
  const toLog = state.lastDone
    ? supersetOf(queue, state.lastDone.exIdx).filter(i => state.results[i]?.[state.lastDone.setIdx]) : [];
  const bigBtn = 'w-full rounded-full h-[68px] text-[14px] font-bold tracking-[0.2em] uppercase flex items-center justify-center gap-3 disabled:opacity-50';
  const restFrac = state.restSeconds > 0 ? Math.max(0, restLeft / (state.restSeconds * 1000)) : 0;
  const restEnding = restLeft <= 5000;
  const progressPct = `${(doneSets / totalSets) * 100}%`;

  // The current phase's buttons: under the content on wider screens, pinned at the bottom on phones.
  const actions = {
    intro: (
      <div className="space-y-3">
        {saved && (
          <button onClick={resumeSaved} className={`${bigBtn} cta-primary`} data-testid="resume-workout">
            Kontynuuj ({formatTime(clockNow(saved.clock))})
          </button>
        )}
        <button onClick={() => { clearSaved(key); setSaved(null); startWorkout(); }}
          className={`${bigBtn} ${saved ? 'cta-ghost' : 'cta-primary'}`} data-testid="start-workout">
          {saved ? 'Zacznij od nowa' : <><Play className="w-5 h-5 fill-current" />Rozpocznij trening</>}
        </button>
      </div>
    ),
    ready: (
      <div className="space-y-3">
        <button onClick={startSet} className={`${bigBtn} cta-primary`} data-testid="start-set">
          <Play className="w-5 h-5 fill-current" /> Start
        </button>
        <div className="flex justify-between">
          <button onClick={skipExercise} className="text-sm text-[var(--text-muted)] flex items-center gap-2 px-2 py-2"><SkipForward className="w-4 h-4" />{ex.superset ? 'Pomiń super-serię' : 'Pomiń ćwiczenie'}</button>
          <button onClick={finishEarly} className="text-sm text-[var(--text-muted)] flex items-center gap-2 px-2 py-2"><Flag className="w-4 h-4" />Zakończ trening</button>
        </div>
      </div>
    ),
    work: (
      <button onClick={finishSet} className={`${bigBtn} cta-gold`} data-testid="finish-set">
        <Check className="w-5 h-5" /> {noRestNext ? 'Zrobione — dalej' : 'Zrobione — przerwa'}
      </button>
    ),
    rest: (
      <div className="grid grid-cols-[1fr_2fr] gap-3">
        <button onClick={addRest} className={`${bigBtn} cta-ghost`}>
          <Plus className="w-4 h-4" /> 15 s
        </button>
        <button onClick={skipRest} className={`${bigBtn} cta-primary`} data-testid="skip-rest">
          Pomiń przerwę
        </button>
      </div>
    ),
    summary: (
      <button onClick={saveWorkout} disabled={saving || doneSets === 0}
        className={`${bigBtn} cta-primary`} data-testid="save-workout">
        {saving ? 'Zapisywanie...' : 'Zapisz trening'}
      </button>
    ),
  }[phase];

  return (
    <div className={`${shell} client-frame flex flex-col`} data-testid="workout-player">
      <GlowBackground />

      {/* Top bar: exit, progress, session stopwatch, pause */}
      <header className="client-frame-top app-content sm:sticky top-0 z-10 px-4 pb-1 sm:pb-0">
        <div className="glass glass-float rounded-full max-w-5xl mx-auto p-1.5 flex items-center justify-between gap-3">
          <button onClick={exit} className="cta-ghost w-11 h-11 rounded-full flex items-center justify-center" aria-label="Wyjdź"><X className="w-5 h-5" /></button>
          {phase !== 'intro' && (
            <div className="hidden sm:flex flex-1 max-w-sm flex-col gap-1.5">
              <div className="flex justify-between text-[11px] text-[var(--text-muted)]"><span>Postęp treningu</span><span className="text-[var(--text-primary)]">{doneSets} / {totalSets} serii</span></div>
              <div className="h-1 rounded-full bg-[var(--track)] overflow-hidden">
                <div className="h-full bg-[#C9A84C] gold-glow transition-all" style={{ width: progressPct }} />
              </div>
            </div>
          )}
          <div className="text-center">
            <div className="label-text text-[var(--text-muted)] text-[0.55rem]">CZAS TRENINGU</div>
            <div className="big-num text-2xl tabular-nums" data-testid="session-time">{formatTime(elapsed)}</div>
          </div>
          {active ? (
            <button onClick={togglePause} className="cta-primary w-11 h-11 rounded-full flex items-center justify-center" aria-label={running ? 'Pauza' : 'Wznów'} data-testid="pause-toggle">
              {running ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
          ) : <span className="w-11" />}
        </div>
        {phase !== 'intro' && (
          <div className="sm:hidden mx-2 mt-2 h-[3px] rounded-full bg-[var(--track)] overflow-hidden">
            <div className="h-full bg-[#C9A84C] gold-glow transition-all" style={{ width: progressPct }} />
          </div>
        )}
      </header>

      <main className="client-frame-scroll app-content flex-1 w-full max-w-5xl mx-auto px-4 py-6 flex flex-col">
        {phase === 'intro' && (
          <div className="flex-1 flex flex-col max-w-2xl w-full mx-auto">
            <p className="label-text text-[#C9A84C] mb-3">DZIEŃ {dayNumber}</p>
            <h1 className="big-num text-4xl sm:text-5xl mb-6">{plan.plan_name}</h1>
            <ol className="space-y-2 mb-8">
              {queue.map((q, i) => (
                <li key={i} className="glass rounded-2xl px-4 py-3.5 flex items-center justify-between gap-3">
                  <span className="min-w-0">
                    {(q.block || q.superset) && (
                      <span className="block text-xs font-semibold text-[#C9A84C] mb-0.5">{q.superset ? `Super-seria ${q.superset}` : q.block}</span>
                    )}
                    {i + 1}. {q.name}
                  </span>
                  <span className="text-sm text-[var(--text-muted)] whitespace-nowrap">{q.sets} × {q.reps || '—'}</span>
                </li>
              ))}
            </ol>
            <div className="hidden sm:block mt-auto">{actions}</div>
          </div>
        )}

        {(phase === 'ready' || phase === 'work') && (
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-5 lg:gap-7 items-start">
            {(ex.animation_url || ex.video_url || ex.image_url) && (
              <section className="glass rounded-[28px] p-3 sm:p-5">
                <ExerciseMedia ex={ex} />
              </section>
            )}

            <section className="glass rounded-[28px] p-5 sm:p-8 flex flex-col gap-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="label-text text-[var(--text-muted)] mb-2">
                    {ex.superset ? `SUPER-SERIA ${ex.superset} · ${group.indexOf(exIdx) + 1} Z ${group.length}` : `ĆWICZENIE ${exIdx + 1} / ${queue.length}`}
                  </p>
                  <h2 className="big-num text-3xl sm:text-4xl">{ex.name}</h2>
                </div>
                <Ring value={(setIdx + (phase === 'work' ? 0.5 : 0)) / ex.sets} size={84} gold>
                  <span className="big-num text-xl" data-testid="set-counter">{setIdx + 1}<span className="text-[var(--text-muted)]">/{ex.sets}</span></span>
                  <span className="text-[9px] tracking-[0.25em] text-[var(--text-muted)] mt-1">SERIA</span>
                </Ring>
              </div>
              <PlanChips ex={ex} />
              {noRestNext && ex.superset && <p className="text-sm font-semibold text-[#C9A84C]">Bez przerwy, potem: {queue[after.exIdx].name}</p>}
              {ex.library && (
                <button onClick={() => setPreview(ex.library)} className="chip w-fit">
                  <Info className="w-3.5 h-3.5" /> Jak wykonać?
                </button>
              )}
              {ex.notes && (
                <div className="p-4 rounded-2xl border border-[#C9A84C]/40 bg-[#C9A84C]/[0.06]">
                  <p className="text-sm leading-relaxed"><span className="font-bold text-[#C9A84C]">Trener: </span>{ex.notes}</p>
                </div>
              )}
              {ex.cues.length > 0 && (
                <ul className="space-y-1">
                  {ex.cues.slice(0, 3).map((c, i) => <li key={i} className="text-sm text-[var(--text-muted)]">• {c}</li>)}
                </ul>
              )}

              <div className="mt-2 space-y-3">
                {phase === 'work' && (
                  <div className="glass-soft rounded-3xl py-5 text-center">
                    <div className="label-text text-[var(--text-muted)] text-[0.6rem]">SERIA TRWA</div>
                    <div className="big-num text-5xl tabular-nums mt-2">{formatTime(elapsed - state.phaseStartedAt)}</div>
                  </div>
                )}
                <div className="hidden sm:block">{actions}</div>
              </div>
            </section>
          </div>
        )}

        {phase === 'rest' && (
          <div className="flex-1 flex flex-col max-w-xl w-full mx-auto gap-5">
            <div className="flex justify-center pt-2">
              <div className="relative" style={{ width: 260, height: 260 }}>
                <svg viewBox="0 0 120 120" width="260" height="260" aria-hidden="true">
                  <circle cx="60" cy="60" r="54" fill="none" stroke="var(--track)" strokeWidth="3" />
                  <circle cx="60" cy="60" r="54" fill="none" stroke={restEnding ? '#C9A84C' : 'var(--chart-main)'} strokeWidth="3"
                    strokeLinecap="round" strokeDasharray={`${(restFrac * 339.3).toFixed(1)} 339.3`} transform="rotate(-90 60 60)"
                    className={restEnding ? 'gold-glow' : ''} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                  <div className="label-text text-[var(--text-muted)]">PRZERWA</div>
                  <div className={`big-num text-7xl tabular-nums ${restEnding ? 'text-[#C9A84C]' : ''}`} data-testid="rest-timer">
                    {formatTime(restLeft)}
                  </div>
                </div>
              </div>
            </div>

            {toLog.map(i => (
              <div key={i} className="glass rounded-3xl p-5">
                <p className="label-text text-[var(--text-muted)] text-[0.6rem] mb-3">
                  ZAPISZ SERIĘ {state.lastDone.setIdx + 1} — {queue[i].name}
                </p>
                <SetInputs value={state.results[i]?.[state.lastDone.setIdx]}
                  onChange={v => setResult(i, state.lastDone.setIdx, v)} />
              </div>
            ))}

            {upcoming && (
              <div className="glass rounded-2xl px-5 py-4">
                <p className="label-text text-[var(--text-muted)] text-[0.55rem]">NASTĘPNIE</p>
                <p className="font-bold mt-1.5">{queue[upcoming.exIdx].name} · seria {upcoming.setIdx + 1}/{queue[upcoming.exIdx].sets}</p>
              </div>
            )}

            <div className="hidden sm:block mt-auto">{actions}</div>
          </div>
        )}

        {phase === 'summary' && (
          <div className="flex-1 flex flex-col max-w-2xl w-full mx-auto">
            <h1 className="big-num text-4xl sm:text-5xl mb-3">Trening zakończony</h1>
            <p className="text-[var(--text-muted)] mb-6">
              Czas: <span className="text-[var(--text-primary)]">{formatTime(elapsed)}</span> · Serie: <span className="text-[var(--text-primary)]">{doneSets} / {totalSets}</span>
            </p>
            <div className="space-y-4 mb-6">
              {queue.map((q, e) => {
                const sets = state.results[e] || [];
                return (
                  <div key={e} className="glass rounded-3xl p-5">
                    <div className="flex justify-between gap-3 mb-3">
                      <h3 className="font-extrabold text-lg">{q.name}</h3>
                      <span className="text-sm text-[var(--text-muted)] whitespace-nowrap">{sets.filter(Boolean).length}/{q.sets} serii</span>
                    </div>
                    {sets.filter(Boolean).length === 0
                      ? <p className="text-sm text-[var(--text-muted)]">Pominięte</p>
                      : sets.map((s, i) => s && (
                        <div key={i} className="flex items-end gap-3 mb-2">
                          <span className="text-sm text-[var(--text-muted)] w-14 pb-2">Seria {i + 1}</span>
                          <div className="flex-1"><SetInputs compact value={s} onChange={v => setResult(e, i, v)} /></div>
                        </div>
                      ))}
                  </div>
                );
              })}
            </div>
            <label className="label-text text-[var(--text-muted)] mb-2 block">JAK SIĘ CZUŁEŚ?</label>
            <Textarea value={state.overallNotes} onChange={e => setState(s => ({ ...s, overallNotes: e.target.value }))}
              placeholder="Samopoczucie, ból, co szło dobrze..." rows={3}
              className="bg-[var(--glass-soft)] border-[var(--glass-soft-border)] rounded-2xl text-[var(--text-primary)] mb-6" />
            <div className="hidden sm:block">{actions}</div>
          </div>
        )}
      </main>

      {actions && <div className="client-frame-bottom sm:hidden app-content px-4 pt-3">{actions}</div>}

      {/* Paused overlay */}
      {active && !running && (
        <div className="fixed inset-0 z-20 bg-black/85 lg:backdrop-blur-md flex flex-col items-center justify-center gap-6">
          <div className="big-num text-5xl" style={{ color: '#FFFFFF' }}>PAUZA</div>
          <button onClick={togglePause} className="rounded-full h-16 px-12 text-base font-bold tracking-[0.2em] uppercase flex items-center gap-3"
            style={{ background: '#FFFFFF', color: '#0A0A0A', boxShadow: '0 0 40px rgba(255,255,255,0.25)' }}>
            <Play className="w-5 h-5 fill-current" /> Wznów
          </button>
          <button onClick={exit} className="text-sm" style={{ color: '#D4D4D4' }}>Wyjdź (postęp zapamiętany)</button>
        </div>
      )}

      <ExerciseDetailDialog exercise={preview} open={!!preview} onOpenChange={open => !open && setPreview(null)} />
    </div>
  );
};
